import { useState } from "react";
import { CalendarCheck, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { serviceOptions, timeOptions } from "./data";

const inputCls =
    "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800 placeholder:text-slate-400 outline-none transition-colors duration-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-100";

const Field = ({ label, required, children, htmlFor }) => (
    <div>
        <label htmlFor={htmlFor} className="block text-sm font-semibold text-slate-700 mb-1.5">
            {label} {required && <span className="text-teal-600">*</span>}
        </label>
        {children}
    </div>
);

export const Appointment = () => {
    const [form, setForm] = useState({
        name: "", phone: "", email: "", service: "", date: "", time: "", concern: "",
    });

    const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target ? e.target.value : e }));

    const handleSubmit = (e) => {
        e.preventDefault();
        toast.success("Your appointment details are ready.", {
            description: "WhatsApp booking will be connected in the next update. You can also call the clinic directly.",
        });
    };

    return (
        <section id="appointment" className="bg-[#f0f9ff] py-20 sm:py-24">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="max-w-3xl mx-auto">
                    <div className="text-center mb-10">
                        <p className="text-xs font-bold uppercase tracking-[0.22em] text-teal-600 mb-4">Appointments</p>
                        <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">
                            Book Your Dental Appointment
                        </h2>
                        <p className="mt-4 text-base leading-relaxed text-slate-600">
                            Fill in your details and preferred appointment time. Your booking request will open in WhatsApp for confirmation.
                        </p>
                    </div>

                    <form
                        onSubmit={handleSubmit}
                        data-testid="appointment-form"
                        className="bg-white rounded-3xl p-7 sm:p-10 shadow-[0_20px_50px_rgba(2,44,40,0.08)] border border-sky-100"
                    >
                        <div className="grid sm:grid-cols-2 gap-5">
                            <Field label="Full Name" required htmlFor="appt-name">
                                <input
                                    id="appt-name"
                                    data-testid="appointment-name-input"
                                    type="text"
                                    required
                                    placeholder="Your full name"
                                    value={form.name}
                                    onChange={set("name")}
                                    className={inputCls}
                                />
                            </Field>
                            <Field label="Phone Number" required htmlFor="appt-phone">
                                <input
                                    id="appt-phone"
                                    data-testid="appointment-phone-input"
                                    type="tel"
                                    required
                                    placeholder="Your phone number"
                                    value={form.phone}
                                    onChange={set("phone")}
                                    className={inputCls}
                                />
                            </Field>
                            <Field label="Email Address" htmlFor="appt-email">
                                <input
                                    id="appt-email"
                                    data-testid="appointment-email-input"
                                    type="email"
                                    placeholder="you@example.com (optional)"
                                    value={form.email}
                                    onChange={set("email")}
                                    className={inputCls}
                                />
                            </Field>
                            <Field label="Select Service" required htmlFor="appt-service">
                                <Select value={form.service} onValueChange={set("service")} required>
                                    <SelectTrigger id="appt-service" data-testid="appointment-service-select" className="rounded-xl border-slate-200 px-4 py-3 h-auto text-sm focus:ring-2 focus:ring-teal-100 focus:border-teal-500">
                                        <SelectValue placeholder="Choose a service" />
                                    </SelectTrigger>
                                    <SelectContent className="bg-white rounded-xl border-slate-200 shadow-[0_10px_40px_rgb(0,0,0,0.1)]">
                                        {serviceOptions.map((s) => (
                                            <SelectItem key={s} value={s} data-testid={`service-option-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="text-sm">
                                                {s}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </Field>
                            <Field label="Preferred Date" required htmlFor="appt-date">
                                <input
                                    id="appt-date"
                                    data-testid="appointment-date-input"
                                    type="date"
                                    required
                                    value={form.date}
                                    onChange={set("date")}
                                    className={inputCls}
                                />
                            </Field>
                            <Field label="Preferred Time" required htmlFor="appt-time">
                                <Select value={form.time} onValueChange={set("time")} required>
                                    <SelectTrigger id="appt-time" data-testid="appointment-time-select" className="rounded-xl border-slate-200 px-4 py-3 h-auto text-sm focus:ring-2 focus:ring-teal-100 focus:border-teal-500">
                                        <SelectValue placeholder="Choose a time" />
                                    </SelectTrigger>
                                    <SelectContent className="bg-white rounded-xl border-slate-200 shadow-[0_10px_40px_rgb(0,0,0,0.1)] max-h-64">
                                        {timeOptions.map((t) => (
                                            <SelectItem key={t} value={t} data-testid={`time-option-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="text-sm">
                                                {t}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </Field>
                            <div className="sm:col-span-2">
                                <Field label="Dental Concern" htmlFor="appt-concern">
                                    <textarea
                                        id="appt-concern"
                                        data-testid="appointment-concern-textarea"
                                        rows={4}
                                        placeholder="Briefly describe your dental concern (optional)"
                                        value={form.concern}
                                        onChange={set("concern")}
                                        className={`${inputCls} resize-none`}
                                    />
                                </Field>
                            </div>
                        </div>

                        <button
                            type="submit"
                            data-testid="appointment-submit-btn"
                            className="mt-7 w-full inline-flex items-center justify-center gap-2.5 rounded-full bg-teal-600 px-8 py-4 text-base font-semibold text-white shadow-[0_12px_30px_rgba(13,148,136,0.4)] transition-all duration-200 hover:bg-teal-700 hover:-translate-y-0.5"
                        >
                            <MessageCircle className="h-5 w-5" />
                            Book Appointment on WhatsApp
                        </button>
                        <p className="mt-4 text-center text-xs leading-relaxed text-slate-400" data-testid="appointment-whatsapp-note">
                            Your appointment details will open in WhatsApp. You can review the message and manually send it to the clinic.
                        </p>
                    </form>
                </div>
            </div>
        </section>
    );
};
